import React, { useState } from 'react';
import { MessageSquare, Send, Clock, User, X } from 'lucide-react';

const ViewPatientQuery = ({ onBack }) => {
  // Sample patient queries data
  const [patientQueries] = useState([
    {
      id: 1,
      patientId: 'PT001',
      patientName: 'Rajesh Kumar',
      question: 'When will my blood test reports be available? I did the test yesterday morning.',
      timestamp: '2025-09-10 14:30',
      status: 'pending',
      responses: []
    },
    {
      id: 2,
      patientId: 'PT003',
      patientName: 'Amit Verma',
      question: 'Do I need to fast before my lipid profile test?',
      timestamp: '2025-09-09 10:15',
      status: 'answered',
      responses: [
        {
          id: 1,
          doctor: 'Dr. Sharma',
          answer: 'Yes, you need to fast for 10-12 hours before the lipid profile test. Only water is allowed during fasting.',
          timestamp: '2025-09-09 11:30'
        }
      ]
    },
    {
      id: 3,
      patientId: 'PT005',
      patientName: 'Vikram Singh',
      question: 'My ECG report shows sinus rhythm. Is this normal?',
      timestamp: '2025-09-11 09:45',
      status: 'pending',
      responses: []
    }
  ]);

  const [selectedQuery, setSelectedQuery] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const handleAnswerSubmit = (queryId) => {
    if (!answerText.trim()) return;
    
    // In a real application, this would be an API call to save the answer
    const updatedQueries = patientQueries.map(query => {
      if (query.id === queryId) {
        return {
          ...query,
          status: 'answered',
          responses: [
            ...query.responses,
            {
              id: query.responses.length + 1,
              doctor: 'Dr. Smith', // This would be the logged-in doctor's name
              answer: answerText.trim(),
              timestamp: new Date().toLocaleString()
            }
          ]
        };
      }
      return query;
    });
    
    // In a real app, we would update state with the response from API
    setAnswerText('');
    setShowSuccessAlert(true);
    
    // Find and update the selected query
    const updatedQuery = updatedQueries.find(q => q.id === queryId);
    setSelectedQuery(updatedQuery);

    // Auto-hide the alert after 3 seconds
    setTimeout(() => {
      setShowSuccessAlert(false);
    }, 3000);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 relative">
      {/* Success Alert Modal */}
      {showSuccessAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-11/12 max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-green-800">Success!</h3>
              <button 
                onClick={() => setShowSuccessAlert(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <p className="text-gray-700">Your response has been submitted successfully!</p>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowSuccessAlert(false)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Patient Queries</h2>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Query List */}
        <div className="lg:col-span-1 bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-4">All Queries</h3>
          <div className="space-y-3">
            {patientQueries.map(query => (
              <div
                key={query.id}
                className={`p-4 rounded-lg cursor-pointer transition-colors ${
                  selectedQuery?.id === query.id
                    ? 'bg-blue-100 border border-blue-300'
                    : 'bg-white border border-gray-200 hover:bg-blue-50'
                }`}
                onClick={() => setSelectedQuery(query)}
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium text-gray-800">{query.patientName}</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      query.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {query.status === 'pending' ? 'Pending' : 'Answered'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 mb-2">{query.question}</p>
                <div className="flex items-center text-xs text-gray-500">
                  <Clock className="w-3 h-3 mr-1" />
                  {query.timestamp}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Query Detail and Response */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-4">
          {selectedQuery ? (
            <>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-800">
                    Query from {selectedQuery.patientName}
                  </h3>
                  <span
                    className={`px-3 py-1 rounded-full text-sm ${
                      selectedQuery.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {selectedQuery.status === 'pending' ? 'Pending Response' : 'Answered'}
                  </span>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-start mb-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{selectedQuery.patientName}</p>
                      <p className="text-sm text-gray-500">{selectedQuery.timestamp}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 mt-2">{selectedQuery.question}</p>
                </div>

                {/* Previous Responses */}
                {selectedQuery.responses.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-700 mb-3">Previous Responses</h4>
                    {selectedQuery.responses.map(response => (
                      <div key={response.id} className="bg-blue-50 rounded-lg p-4 mb-3">
                        <div className="flex items-start mb-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{response.doctor}</p>
                            <p className="text-sm text-gray-500">{response.timestamp}</p>
                          </div>
                        </div>
                        <p className="text-gray-700 mt-2">{response.answer}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Answer Input */}
                <div>
                  <h4 className="font-medium text-gray-700 mb-3">Your Response</h4>
                  <textarea
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    placeholder="Type your answer here..."
                    className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={() => handleAnswerSubmit(selectedQuery.id)}
                    disabled={!answerText.trim()}
                    className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Send Response
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <MessageSquare className="w-12 h-12 mb-4 text-gray-400" />
              <p>Select a patient query to view details and respond</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewPatientQuery;